import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import NavHeader from '../shared/Nav-Header';
import styles from './profile.module.css'; // Use CSS modules

const CompanyProfile = () => {
    const [profile, setProfile] = useState({
        name: '',
        location: '',
        description: '',
        website: '',
        logo: '',
        contact: {
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            country: ''
        },
        profileCreatedBy: {
            name: '',
            id: ''
        },
        recruiter: {
            name: '',
            id: ''
        },
        postedJobs: {},
        status: {},
        liveJobs: [],
        hiddenJobs: [],
        pausedJobs: [],
        draftJobs: [],
        deletedJobs: [],
        established: null,
        additionalDetails: {
            size: '',
            industry: '',
            type: ''
        },
        benefits: [],
        perks: [],
        team: [],
        values: '',
        mission: '',
        vision: '',
        culture: '',
        awards: [],
        reviews: [],
        ratings: null,
        socialMedia: {}
    });
    const [isEditing, setIsEditing] = useState(false);
    const [notification, setNotification] = useState('');
    const [uploading, setUploading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    localStorage.setItem('notification', 'Please login first');
                    navigate('/recruiter/login');
                    return;
                }

                const response = await axios.get('/api/company/get-company-profile-details', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setProfile(response.data);
            } catch (error) {
                console.error('Error fetching profile:', error);
            }
        };

        fetchProfile();
    }, [navigate]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isEditing) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes. Do you want to leave?';
            }
        };
    
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isEditing]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', 'ml_default'); // Replace with your Cloudinary upload preset
        setUploading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/upload/upload-all', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            const url = response.data.url;

            setProfile({ ...profile, logo: url });
            setUploading(false);
        } catch (error) {
            console.error('Error uploading image:', error);
            setNotification('Failed to upload image.');
        }
    };

    const handleChange = (e) => {
        setIsEditing(true);
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setProfile((prevProfile) => ({
                ...prevProfile,
                [parent]: {
                    ...prevProfile[parent],
                    [child]: value
                }
            }));
        } else {
            setProfile({ ...profile, [name]: value });
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleSaveClick = async () => {
        try {
            const token = localStorage.getItem('token');

            const response = await axios.put('/api/company/update-company-profile', 
                profile, 
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            
            });
            setNotification(response.data.message);
            setIsEditing(false);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error updating profile:';
            console.error(errorMessage);
        }
    };

    const handleCancelClick = () => {
        setIsEditing(false);
    };

    return (
        <div>
            <NavHeader />
            <div className={styles.profileContainer}>
                <h2>Company Profile</h2>
                {notification && <p className={styles.notification}>{notification}</p>}
                <div className={styles.profileInfo}>
                    <label>Name:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="name"
                            value={profile.name}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.name}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Location:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="location"
                            value={profile.location}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.location}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Description:</label>
                    {isEditing ? (
                        <textarea
                            name="description"
                            value={profile.description}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.description}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Website:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="website"
                            value={profile.website}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.website}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Company's Logo</label>
                    <img
                        src={profile.logo ? profile.logo.replace('/upload/', '/upload/w_480,h_500,c_fill/') : 'https://via.placeholder.com/150'}
                        alt="logo"
                        className={styles.logo}
                    />
                    {isEditing && (
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className={styles.fileInput}
                        />
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Contact Email:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="contact.email"
                            value={profile.contact.email}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.contact.email}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Contact Phone:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="contact.phone"
                            value={profile.contact.phone}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.contact.phone}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Contact Address:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="contact.address"
                            value={profile.contact.address}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.contact.address}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Contact City:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="contact.city"
                            value={profile.contact.city}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.contact.city}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Contact State:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="contact.state"
                            value={profile.contact.state}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.contact.state}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Contact Zip:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="contact.zip"
                            value={profile.contact.zip}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.contact.zip}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Contact Country:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="contact.country"
                            value={profile.contact.country}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.contact.country}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Established:</label>
                    {isEditing ? (
                        <input
                            type="date"
                            name="established"
                            value={profile.established ? profile.established.split('T')[0] : ''}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.established ? profile.established.split('T')[0] : ''}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Company Size:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="additionalDetails.size"
                            value={profile.additionalDetails.size}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.additionalDetails.size}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Industry:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="additionalDetails.industry"
                            value={profile.additionalDetails.industry}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.additionalDetails.industry}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Company Type:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="additionalDetails.type"
                            value={profile.additionalDetails.type}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.additionalDetails.type}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Values:</label>
                    {isEditing ? (
                        <textarea
                            name="values"
                            value={profile.values}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.values}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Mission:</label>
                    {isEditing ? (
                        <textarea
                            name="mission"
                            value={profile.mission}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.mission}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Vision:</label>
                    {isEditing ? (
                        <textarea
                            name="vision"
                            value={profile.vision}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.vision}</p>
                    )}
                </div>
                <div className={styles.profileInfo}>
                    <label>Culture:</label>
                    {isEditing ? (
                        <textarea
                            name="culture"
                            value={profile.culture}
                            onChange={handleChange}
                        />
                    ) : (
                        <p>{profile.culture}</p>
                    )}
                </div>
                {isEditing ? (
                    <div className={styles.profileActions}>
                        <button onClick={handleSaveClick} disabled={uploading} className={styles.saveButton}>Save</button>
                        <button onClick={handleCancelClick}>Cancel</button>
                    </div>
                ) : (
                    <button onClick={handleEditClick}>Edit</button>
                )}
            </div>
        </div>
    );
};

export default CompanyProfile;